<template>
    <div class="loading-page" :class="{ [STATE_CLASSES.VISIBLE]: visible }">
        <div class="loading-page__content">
            <figure class="loading-page__icon">
                <loading-icon type="dots" :color="iconColor" />
            </figure>

            <span class="loading-page__message">
                {{ message }}
            </span>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';

import appThemes from '@/data/app-themes';
import { STATE_CLASSES } from '@/data/constants/classes';
import LoadingIcon from './loading-icon';

const DEFAULT_MESSAGE = 'Now Loading';

export default {
    name: 'loading-page',
    components: { LoadingIcon },

    props: {
        overrideMessage: {
            type: String,
            default: '',
        },

        permanent: {
            type: Boolean,
            default: false,
        },

        visibleDefault: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            interval: 0,
            message: DEFAULT_MESSAGE,
            messages: [
                'Adding Hidden Agendas',
                'Adjusting Bell Curves',
                'Aesthesizing Industrial Areas',
                'Aligning Covariance Matrices',
                'Applying Feng Shui Shaders',
                'Applying Theatre Soda Layer',
                'Asserting Packed Exemplars',
                'Attempting to Lock Back-Buffer',
                'Binding Sapling Root System',
                'Breeding Fauna',
                'Building Data Trees',
                'Bureacritizing Bureaucracies',
                'Calculating Inverse Probability Matrices',
                'Calculating Llama Expectoration Trajectory',
                'Calibrating Blue Skies',
                'Charging Ozone Layer',
                'Coalescing Cloud Formations',
                'Cohorting Exemplars',
                'Collecting Meteor Particles',
                'Compounding Inert Tessellations',
                'Compressing Fish Files',
                'Computing Optimal Bin Packing',
                'Concatenating Sub-Contractors',
                'Containing Existential Buffer',
                'Debarking Ark Ramp',
                'Debunching Unionized Commercial Services',
                'Deciding What Message to Display Next',
                'Decomposing Singular Values',
                'Decrementing Tectonic Plates',
                'Deleting Dee Routes',
                'Depixelating Inner Mountain Surface Back Faces',
                'Depositing Slush Funds',
                'Destabilizing Economic Indicators',
                'Determining Width of Blast Fronts',
                'Dicing Models',
                'Downloading Satellite Terrain Data',
                'Exposing Flash Variables to Martin System',
                'Extracting Resources',
                'Flushing Pipe Network',
                'Gathering Particle Sources',
                'Generating Jobs',
                'Gesticulating Mimes',
                'Graphing Whale Migration',
                'Hiding Andrew Webnet Mask',
                'Increasing Accuracy of RCI Simulators',
                'Increasing Magmafacation',
                'Initializing Rhinoceros Breeding Timetable',
                'Initializing Robotic Click-Path AI',
                'Inserting Sublimated Messages',
                'Integrating Curves',
                'Integrating Illumination Form Factors',
                'Integrating Population Graphs',
                'Iterating Cellular Automata',
                'Lecturing Errant Subsystems',
                'Modeling Object Components',
                'Mopping Occupant Leaks',
                'Normalizing Power',
                'Obfuscating Broderick Matrix',
                'Partitioning City Grid Singularities',
                'Perturbing Matrices',
                'Populating Lot Templates',
                'Preparing Sprites for Random Walks',
                'Prioritizing Landmarks',
                'Projecting Jamie Pastry Intake',
                'Realigning Alternate Time Frames',
                'Reconfiguring User Mental Processes',
                'Relaxing Splines',
                'Removing Road Network Speed Bumps',
                'Removing Texture Gradients',
                'Removing Vehicle Avoidance Behavior',
                'Resolving GUID Conflict',
                'Reticulating Splines',
                'Retracting Uy Shader',
                'Retrieving from Back Store',
                'Reverse Engineering Image Consultant',
                'Routing Neural Network Infrastructure',
                'Scattering Rhino Food Sources',
                'Scrubbing Terrain',
                'Searching for Llamas',
                'Seeding Architecture Simulation Parameters',
                'Sequencing Particles',
                'Setting Advisor Moods',
                'Setting Sunita Indicators',
                'Setting Universal Physical Constants',
                'Sonically Enhancing Occupant-Free Timber',
                'Speculating Stock Market Indices',
                'Splatting Transforms',
                'Stratifying Ground Layers',
                'Sub-Sampling Water Data',
                'Synthesizing Gravity',
                'Synthesizing Wavelets',
                'Time-Compressing Simulator Clock',
                'Unable to Reveal Current Activity',
                'Weathering Buildings',
                'Zeroing Crime Network',
            ],
            STATE_CLASSES,
            visible: true,
        };
    },

    computed: {
        ...mapState({
            appTheme: state => state.page.appTheme,
            pageLoading: state => state.page.pageLoading,
        }),

        iconColor() {
            return appThemes[this.appTheme].app.color;
        },
    },

    watch: {
        pageLoading({ loading, message }) {
            this.visible = loading;

            switch (true) {
                case loading && !!message:
                    this.message = message;
                    break;

                case loading:
                    this.interval = window.setInterval(this.changeMessage, 3000);
                    break;

                default:
                    window.clearTimeout(this.interval);
                    this.message = DEFAULT_MESSAGE;
            }
        },
    },

    created() {
        this.visible = this.visibleDefault;
    },

    mounted() {
        if (this.permanent) {
            this.visible = true;
            this.message = this.overrideMessage;
        }
    },

    methods: {
        changeMessage() {
            const randomIndex = Math.floor(Math.random() * this.messages.length);

            this.message = this.messages[randomIndex];
        },
    },
};
</script>

<style lang="scss">
.loading-page {
    @extend %fill;
    position: fixed;
    background-color: var(--app-backgroundColor);
    opacity: 0;
    transition: opacity 0.45s;
    z-index: -1;

    &.#{ $is-visible } {
        opacity: 0.9;
        z-index: 999999;
    }

    &__content {
        @extend %center-middle;
        opacity: 0;
        text-align: center;

        .#{ $is-visible } & {
            transition: opacity 1s 0.45s;
            opacity: 0.9;
        }
    }

    &__icon {
        display: flex;
        justify-content: center;
        margin: 0;
    }

    &__message {
        display: block;
        margin-top: 20px;
    }
}
</style>
